import { useQuery, gql } from "utils/urql";
import { DoubleWideQuery } from "types/api";
import Image from "components/Image";
import styles from "./DoubleWide.module.scss";
import HomepageMetadata from "./HomepageMetadata";
import Caption from "./Caption";

const query = gql`
  query DoubleWide {
    homepageModule(slug: "double-wide", version: "b1") {
      __typename
      ... on HomepagePromoItem {
        title
        dek
        url
        kicker
        kickerUrl
        authors {
          id
          displayName
          url
        }
        mobileImage: riverImage(
          width: 543
          height: 305
          sizes: ["328w", "382w", "656w", "543w", "764w", "1086w"]
        ) {
          url
          width
          height
          srcSet
          altText
          attributionText
          attributionUrl
        }
        desktopImage: riverImage(
          width: 296
          height: 198
          sizes: ["291w", "296w", "455w", "582w", "592w", "910w"]
        ) {
          srcSet
        }
      }
    }
  }
`;

const DoubleWide = () => {
  const [{ data }] = useQuery<DoubleWideQuery>({ query });

  if (!data || data.homepageModule?.__typename !== "HomepagePromoItem") {
    return null;
  }

  const article = data.homepageModule;

  return (
    <article className={styles.root}>
      <div className={styles.content}>
        {article.kicker &&
          (article.kickerUrl ? (
            <a
              href={article.kickerUrl}
              className={styles.kicker}
              data-action="click link - double wide - kicker"
              dangerouslySetInnerHTML={{
                __html: article.kicker,
              }}
            />
          ) : (
            <div
              className={styles.kicker}
              dangerouslySetInnerHTML={{
                __html: article.kicker,
              }}
            />
          ))}
        <h2 className={styles.title}>
          <a
            href={article.url}
            className={styles.titleLink}
            data-action="click link - double wide - title"
            dangerouslySetInnerHTML={{
              __html: article.title,
            }}
          />
        </h2>
        {article.dek && (
          <p
            className={styles.dek}
            dangerouslySetInnerHTML={{
              __html: article.dek,
            }}
          />
        )}
        <HomepageMetadata
          authors={article.authors}
          analyticsSection="double wide"
        />
      </div>

      {article.mobileImage && (
        <figure className={styles.figure}>
          <a
            className={styles.imageLink}
            href={article.url}
            data-action="click link - double wide - image"
          >
            <picture>
              {article.desktopImage && (
                <source
                  srcSet={article.desktopImage.srcSet}
                  media="(min-width: 576px)"
                  sizes="(min-width: 1296px) 296px, (min-width: 976px) calc(25vw - 32px), calc(50vw - 32px)"
                />
              )}

              <Image
                lazy={true}
                src={article.mobileImage.url}
                srcSet={article.mobileImage.srcSet}
                alt={article.mobileImage.altText}
                width={article.mobileImage.width}
                height={article.mobileImage.height}
                sizes="calc(100vw - 32px)"
              />
            </picture>
          </a>

          <Caption
            attributionText={article.mobileImage.attributionText}
            attributionUrl={article.mobileImage.attributionUrl}
          />
        </figure>
      )}
    </article>
  );
};

export default DoubleWide;
